<template>
  <div class="my-6">
    <!-- Tabs -->
    <div
      class="mb-10 overflow-hidden rounded-3xl border border-orange-100 bg-gradient-to-r from-orange-50 via-white to-white p-8"
    >
      <div
        class="flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
      >
        <!-- Left -->
        <div class="flex items-center gap-5">
          <div
            class="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500 shadow-lg shadow-orange-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="h-8 w-8 text-white"
            >
              <path
                d="M5 4.60434V13.7889C5 15.1263 5.6684 16.3752 6.7812 17.1171L12 20.5963L17.2188 17.1171C18.3316 16.3752 19 15.1263 19 13.7889V4.60434L12 3.04879L5 4.60434ZM3.78307 2.82598L12 1L20.2169 2.82598C20.6745 2.92766 21 3.33347 21 3.80217V13.7889C21 15.795 19.9974 17.6684 18.3282 18.7812L12 23L5.6718 18.7812C4.00261 17.6684 3 15.795 3 13.7889V3.80217C3 3.33347 3.32553 2.92766 3.78307 2.82598ZM12 13.5L9.06107 15.0451L9.62236 11.7725L7.24472 9.45492L10.5305 8.97746L12 6L13.4695 8.97746L16.7553 9.45492L14.3776 11.7725L14.9389 15.0451L12 13.5Z"
              />
            </svg>
          </div>

          <div>
            <h1 class="mt-3 text-3xl font-bold tracking-tight text-[#34495E]">
              Membership Renewal
            </h1>

            <p class="mt-2 max-w-2xl leading-7 text-zinc-600">
              Renew your membership by selecting a subscription plan or track
              the status of an existing renewal using your reference number.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-8 border-b border-zinc-200">
      <nav class="-mb-px flex gap-8">
        <button
          @click="activeTab = 'plans'"
          :class="[
            'border-b-2 px-1 py-4 text-sm font-semibold transition',
            activeTab === 'plans'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-zinc-500 hover:text-secondary',
          ]"
        >
          Subscription Plans
        </button>

        <button
          @click="activeTab = 'tracking'"
          :class="[
            'border-b-2 px-1 py-4 text-sm font-semibold transition',
            activeTab === 'tracking'
              ? 'border-orange-500 text-orange-600'
              : 'border-transparent text-zinc-500 hover:text-secondary',
          ]"
        >
          Tracking Details
        </button>
      </nav>
    </div>

    <!-- Subscription Plans -->
    <div v-if="activeTab === 'plans'" class="mt-8">
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div
          v-for="plan in plans"
          :key="plan.id"
          :class="[
            'relative flex flex-col rounded-2xl p-6 transition-all duration-300',
            plan.current
              ? 'overflow-hidden border-2 border-orange-500 bg-orange-50 shadow-lg'
              : 'border border-zinc-200 bg-white shadow-sm hover:-translate-y-1 hover:shadow-xl',
          ]"
        >
          <!-- Current Badge -->
          <div v-if="plan.current" class="absolute right-0 top-0">
            <span
              class="rounded-bl-xl bg-orange-500 px-4 py-2 text-xs font-semibold tracking-wide text-white"
            >
              CURRENT PLAN
            </span>
          </div>

          <h3 class="text-2xl font-bold text-secondary">
            {{ plan.name }}
          </h3>

          <p class="mt-1 text-sm text-zinc-500">
            {{ plan.description }}
          </p>

          <div class="my-6">
            <span
              :class="[
                'text-4xl font-bold',
                plan.current ? 'text-orange-600' : 'text-secondary',
              ]"
            >
              ₱{{ plan.price.toLocaleString() }}
            </span>
            <span class="text-zinc-500">/year</span>
          </div>

          <ul class="flex-1 space-y-3 text-sm text-zinc-700">
            <li v-for="benefit in plan.benefits" :key="benefit">
              ✔ {{ benefit }}
            </li>
          </ul>

          <a
            href="#"
            class="mt-6 text-center text-sm font-semibold text-orange-600 hover:underline"
          >
            View Plan Details →
          </a>

          <router-link
            @click="submitPlanDetails(plan)"
            to="/payment/center/payment-mode"
            :class="[
              'mt-3 block rounded-xl py-3 text-center font-semibold transition',
              plan.current
                ? 'border border-orange-500 bg-white text-orange-600'
                : 'bg-orange-500 text-white hover:bg-orange-600',
            ]"
          >
            {{ plan.current ? "✓ Renew Plan" : `Upgrade to ${plan.name}` }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Tracking Details -->
    <div
      v-else
      class="mt-8 rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="mx-auto h-16 w-16 text-orange-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M3 7.5l9-4.5 9 4.5M3 7.5v9l9 4.5 9-4.5v-9M12 12v9"
        />
      </svg>

      <h3 class="mt-4 text-xl font-semibold text-secondary">
        Track Your Renewal
      </h3>

      <p class="mt-2 text-zinc-500">
        Enter your reference number or view your recent renewal transactions to
        check the latest payment and renewal status.
      </p>

      <router-link
        to="/payment/center/tracking"
        class="mt-6 inline-flex rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600"
      >
        View Tracking Details
      </router-link>
    </div>
  </div>
</template>
<script>
import { reusablePaymentForm } from "@/utils/composables/payment_center";
import { ref } from "vue";

export default {
  setup() {
    const { submitPlanDetails } = reusablePaymentForm();
    const activeTab = ref("plans");
    const plans = ref([
      {
        id: 1,
        name: "Bronze",
        description: "Ideal for basic healthcare coverage.",
        price: 7000,
        current: true,
        benefits: [
          "₱50,000 Coverage",
          "Outpatient Benefits",
          "Emergency Care",
          "Annual Check-up",
        ],
      },
      {
        id: 2,
        name: "Silver",
        description: "More coverage for growing healthcare needs.",
        price: 8500,
        current: false,
        benefits: [
          "₱100,000 Coverage",
          "Outpatient Benefits",
          "Emergency Care",
          "Annual Check-up",
          "Dental Benefits",
        ],
      },
      {
        id: 3,
        name: "Gold",
        description: "Maximum protection with premium benefits.",
        price: 12500,
        current: false,
        benefits: [
          "₱200,000 Coverage",
          "Outpatient Benefits",
          "Emergency Care",
          "Annual Check-up",
          "Dental Benefits",
          "Telemedicine Access",
        ],
      },
    ]);

    return {
      plans,
      submitPlanDetails,
      activeTab,
    };
  },
};
</script>
